import { createResource, createSignal, For, Show } from "solid-js";
import type { UserPreferences } from "@energyplatform/client";
import type { Locale } from "@energyplatform/i18n";
import { locale, SUPPORTED_LOCALES, t } from "../i18n.ts";
import { session } from "../services.ts";
import {
  preferences,
  reloadPreferences,
  savePreferences,
} from "../preferences.ts";
import { viewProfile } from "../view.ts";
import { Icon } from "../icons.tsx";
import { ErrorBox } from "../components/ErrorBox.tsx";
import { PageHeader, useAction } from "../components/ui.tsx";

const PRIORITIES = [1, 2, 3] as const;
const LATER_CHANNELS = ["email", "sms", "push"] as const;
const LOGIN_PROVIDERS = ["SUPLA", "Lavva", "Exalus"] as const;

/**
 * My profile (prototype "Mój profil"): personal settings that follow the person to every device — language, theme,
 * density and which event priorities notify in the app. Password, two-step verification and sessions belong to the
 * identity provider and open its own page. Channels and features the platform does not have yet are shown greyed.
 */
export function ProfilePage() {
  const user = () => {
    const s = session();
    return s.status === "signedIn" ? s.user : undefined;
  };
  const [run, error, busy] = useAction();
  const [saved, setSaved] = createSignal(false);
  // Read again here, so a failure shows and nothing is saved over settings that could not be read.
  const [loaded] = createResource(() => reloadPreferences());
  const save = (change: Partial<UserPreferences>) =>
    void run(async () => {
      setSaved(false);
      await savePreferences(change);
      setSaved(true);
    });
  const muted = (p: number) => preferences().mutedPriorities.includes(p);
  const toggle = (p: number, on: boolean) =>
    save({
      mutedPriorities: on
        ? preferences().mutedPriorities.filter((x) => x !== p)
        : [...preferences().mutedPriorities, p],
    });

  return (
    <>
      <PageHeader
        eyebrow={user()?.email ?? ""}
        title={t("profilePage.title")}
        sub={t("profilePage.sub")}
      >
        {/* A plain form POST, as in the shell: the host ends the session and signs out at ZITADEL too. */}
        <form method="post" action="/auth/logout">
          <button type="submit" class="btn">
            <Icon name="x" />
            {t("auth.signOut")}
          </button>
        </form>
      </PageHeader>
      <Show when={error() ?? loaded.error}>
        <ErrorBox error={error() ?? loaded.error} />
      </Show>
      <div class="grid g-2">
        <section class="card">
          <div class="card-h">
            <h2>{t("profilePage.look")}</h2>
            <Show when={saved() && !busy()}>
              <span class="meta">{t("profilePage.saved")}</span>
            </Show>
          </div>
          <div class="card-b" style={{ display: "grid", gap: "12px" }}>
            <div class="grid g-2e" style={{ gap: "10px" }}>
              <div class="field">
                <label for="pfName">{t("profilePage.name")}</label>
                <input id="pfName" value={user()?.name ?? ""} disabled />
              </div>
              <div class="field">
                <label for="pfMail">{t("profilePage.email")}</label>
                <input id="pfMail" value={user()?.email ?? ""} disabled />
              </div>
            </div>
            <div class="grid g-2e" style={{ gap: "10px" }}>
              <div class="field">
                <label for="pfLang">{t("profilePage.language")}</label>
                <select
                  id="pfLang"
                  value={preferences().language ?? ""}
                  disabled={busy() || !loaded()}
                  onChange={(e) =>
                    save({
                      language: (e.currentTarget.value ||
                        null) as Locale | null,
                    })
                  }
                >
                  <option value="">
                    {t("profilePage.languageBrowser", {
                      language: t(`lang.${locale()}`),
                    })}
                  </option>
                  <For each={SUPPORTED_LOCALES}>
                    {(l) => <option value={l}>{t(`lang.${l}`)}</option>}
                  </For>
                </select>
              </div>
              <div class="field">
                <label for="pfTheme">{t("profilePage.theme")}</label>
                <select
                  id="pfTheme"
                  value={preferences().theme}
                  disabled={busy() || !loaded()}
                  onChange={(e) =>
                    save({
                      theme: e.currentTarget.value as UserPreferences["theme"],
                    })
                  }
                >
                  <For each={["system", "light", "dark"] as const}>
                    {(x) => (
                      <option value={x}>{t(`profilePage.themes.${x}`)}</option>
                    )}
                  </For>
                </select>
              </div>
            </div>
            <div class="field">
              <label for="pfDensity">{t("profilePage.density")}</label>
              <select
                id="pfDensity"
                value={preferences().density ?? ""}
                disabled={busy() || !loaded()}
                onChange={(e) =>
                  save({
                    density: (e.currentTarget.value ||
                      null) as UserPreferences["density"],
                  })
                }
              >
                <option value="">
                  {t("profilePage.densityProfile", {
                    profile: t(`profile.${viewProfile().id}`),
                  })}
                </option>
                <option value="compact">
                  {t("profilePage.densities.compact")}
                </option>
                <option value="comfortable">
                  {t("profilePage.densities.comfortable")}
                </option>
              </select>
            </div>
            <p class="hint" style={{ margin: 0 }}>
              {t("profilePage.timeZoneHint")}
            </p>
          </div>
        </section>
        <section class="card">
          <div class="card-h">
            <h2>{t("profilePage.notifications")}</h2>
            <a class="meta" href="/events?tab=rules">
              {t("profilePage.rulesLink")}
            </a>
          </div>
          <div class="scrollx">
            <table class="tbl">
              <thead>
                <tr>
                  <th>{t("profilePage.priority")}</th>
                  <th class="c">{t("profilePage.channels.app")}</th>
                  <For each={LATER_CHANNELS}>
                    {(c) => (
                      <th
                        class="c"
                        style={{ opacity: 0.55 }}
                        title={t("reports.soon")}
                      >
                        {t(`profilePage.channels.${c}`)}
                      </th>
                    )}
                  </For>
                </tr>
              </thead>
              <tbody>
                <For each={PRIORITIES}>
                  {(p) => (
                    <tr>
                      <td>
                        <span
                          class={`chip ${p === 1 ? "crit" : p === 2 ? "warn" : ""}`}
                        >
                          {t(`events.prio.${p}`)}
                        </span>
                      </td>
                      <td class="c">
                        <input
                          type="checkbox"
                          aria-label={`${t("profilePage.channels.app")} · ${t(`events.prio.${p}`)}`}
                          checked={p === 1 || !muted(p)}
                          disabled={p === 1 || busy() || !loaded()}
                          title={
                            p === 1 ? t("profilePage.highRequired") : undefined
                          }
                          onChange={(e) => toggle(p, e.currentTarget.checked)}
                        />
                      </td>
                      <For each={LATER_CHANNELS}>
                        {(c) => (
                          <td class="c" style={{ opacity: 0.55 }}>
                            <input
                              type="checkbox"
                              disabled
                              aria-label={`${t(`profilePage.channels.${c}`)} · ${t(`events.prio.${p}`)}`}
                            />
                          </td>
                        )}
                      </For>
                    </tr>
                  )}
                </For>
              </tbody>
            </table>
          </div>
          <div class="card-b" style={{ display: "grid", gap: "10px" }}>
            <p class="hint" style={{ margin: 0 }}>
              {t("profilePage.highRequired")}
            </p>
            <div
              class="grid g-2e"
              style={{ gap: "10px", opacity: 0.55 }}
              title={t("reports.soon")}
            >
              <div class="field">
                <label for="pfQuiet">{t("profilePage.quietHours")}</label>
                <select id="pfQuiet" disabled>
                  <option>{t("profilePage.none")}</option>
                </select>
              </div>
              <div class="field">
                <label for="pfDigest">{t("profilePage.digest")}</label>
                <select id="pfDigest" disabled>
                  <option>{t("profilePage.none")}</option>
                </select>
              </div>
            </div>
            <p class="hint" style={{ margin: 0 }}>
              {t("profilePage.channelsSoon")}
            </p>
          </div>
        </section>
        <section class="card">
          <div class="card-h">
            <h2>{t("profilePage.security")}</h2>
          </div>
          <div class="card-b" style={{ display: "grid", gap: "10px" }}>
            <dl class="kv" style={{ margin: 0 }}>
              <dt>{t("profilePage.roles")}</dt>
              <dd>
                {(user()?.roles ?? []).length === 0
                  ? t("role.none")
                  : user()!
                      .roles.map((r) => t(`role.${r}`))
                      .join(", ")}
              </dd>
            </dl>
            <p class="hint" style={{ margin: 0 }}>
              {t("profilePage.securityHint")}
            </p>
            <Show when={user()?.accountUrl}>
              {(url) => (
                <div>
                  <a
                    class="btn"
                    href={url()}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Icon name="link" />
                    {t("profilePage.account")}
                  </a>
                </div>
              )}
            </Show>
          </div>
        </section>
        <section class="card">
          <div class="card-h">
            <h2>{t("profilePage.linked")}</h2>
            <span class="meta">{t("reports.soon")}</span>
          </div>
          <table class="tbl" style={{ opacity: 0.55 }}>
            <tbody>
              <For each={LOGIN_PROVIDERS}>
                {(p) => (
                  <tr>
                    <td>
                      <Icon name="link" /> {p}
                    </td>
                    <td>
                      <span class="chip">{t("profilePage.notLinked")}</span>
                    </td>
                    <td class="r">
                      <button
                        type="button"
                        class="btn sm"
                        disabled
                        title={t("reports.soon")}
                      >
                        {t("profilePage.link")}
                      </button>
                    </td>
                  </tr>
                )}
              </For>
            </tbody>
          </table>
          <div class="card-b hint">{t("profilePage.linkedHint")}</div>
        </section>
        <section
          class="card"
          style={{ opacity: 0.55 }}
          title={t("reports.soon")}
        >
          <div class="card-h">
            <h2>{t("profilePage.assistant")}</h2>
            <span class="meta">{t("reports.soon")}</span>
          </div>
          <div class="card-b hint">{t("profilePage.assistantHint")}</div>
        </section>
      </div>
    </>
  );
}
